import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { PreviewBanner } from "../PreviewBanner";
import { EvidenceChip } from "../EvidenceChip";
import { QSection, Unavailable } from "./Bits";
import { Explorer } from "./Explorer";

const PILOT = [["philosophy", "Philosophy"], ["economics", "Economics"], ["computer-science", "Computer Science"], ["psychology", "Psychology"], ["law", "Law"]];

export const SubjectSwitcher = ({ current }) => (
  <nav aria-label="Pilot subjects" data-testid="sci-subject-switcher" className="flex flex-wrap gap-2 pt-6">
    {PILOT.map(([slug, name]) => (
      <Link key={slug} to={`/preview/sci/subjects/${slug}`} data-testid={`sci-subject-switch-${slug}`} aria-current={slug === current ? "page" : undefined}
        className={`rounded-full px-3 py-1 text-xs ${slug === current ? "bg-[#002147] text-white" : "border border-stone-300 text-stone-700 hover:border-[#002147]"}`}>{name}</Link>))}
  </nav>
);

const Sentence = ({ c, testId }) => (
  <p data-testid={testId} className="text-[15px] leading-relaxed text-stone-800">{c.value}<EvidenceChip claim={c} subtle testId={`${testId}-source`} /></p>
);

function Block({ title, rows, prefix }) {
  return (
    <div>
      <h3 className="mb-3 text-sm font-semibold text-stone-900">{title}</h3>
      {rows.length ? <div className="space-y-3">{rows.map((c, i) => <Sentence key={i} c={c} testId={`${prefix}-${i}`} />)}</div>
        : <Unavailable testId={`${prefix}-none`}>Not verified yet.</Unavailable>}
    </div>
  );
}

// Concise benchmark summary + course explorer for pilot subjects whose full guide is still being researched.
export function SciSubjectLite({ d }) {
  const s = d.summary;
  return (
    <div className="bg-[#FDFBF7]" data-testid="sci-subject-lite">
      <Helmet><meta name="robots" content="noindex,nofollow" /><title>{`${d.subject.name} · private preview`}</title></Helmet>
      <PreviewBanner runId={d.run_ids.join(" + ")} />
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <SubjectSwitcher current={d.subject.slug} />
        <header className="py-12 lg:py-16">
          <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-[#92400E]">Subject summary{d.subject.family ? ` · ${d.subject.family}` : ""}</p>
          <h1 className="mt-3 font-editorial text-5xl text-stone-900 sm:text-6xl" data-testid="sci-subject-title">{d.subject.name}</h1>
          <p className="mt-4 max-w-2xl text-sm text-stone-600" data-testid="sci-guide-status">{d.guide_status}</p>
        </header>
        <QSection id="summary" n={1} title={`What is ${d.subject.name}?`} lede={d.source ? `From the ${d.source.publisher} subject benchmark statement.` : undefined}>
          <div className="grid gap-10 md:grid-cols-2">
            <Block title="The subject" rows={s.what_is} prefix="sci-lite-what" />
            <Block title="What the degree trains you to do" rows={s.skills} prefix="sci-lite-skill" />
            <Block title="How it's taught" rows={s.teaching} prefix="sci-lite-teach" />
            <Block title="Where it leads" rows={s.careers} prefix="sci-lite-career" />
          </div>
          {!!d.topic_areas.length && (
            <div className="mt-10"><h3 className="mb-3 text-sm font-semibold text-stone-900">Areas UK degrees typically cover</h3>
              <ul className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">{d.topic_areas.map((c, i) => (
                <li key={i} data-testid={`sci-lite-topic-${i}`} className="flex items-start justify-between gap-2 rounded-xl border border-stone-200 bg-white p-3 text-sm text-stone-800">
                  <span>{c.value.charAt(0).toUpperCase() + c.value.slice(1)}</span><EvidenceChip claim={c} subtle testId={`sci-lite-topic-${i}-source`} /></li>))}</ul></div>)}
        </QSection>
        <QSection id="where" n={2} title={`Where can I study ${d.subject.name}?`} lede="Signed in, courses are ordered by your own course fit; otherwise A to Z while the WUA ranking is in development.">
          <Explorer slug={d.subject.slug} />
        </QSection>
      </div>
    </div>
  );
}
